import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../components/AppShell';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { ArrowLeft, Slack, FileCode, GitBranch, ExternalLink, Trash2, Workflow, Plus } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import InlineRenameInput from '../components/InlineRenameInput';

export default function IntegrationsPage() {
  const navigate = useNavigate();
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [instanceToDelete, setInstanceToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadSources();
  }, []);

  const loadSources = async () => {
    try {
      const response = await api.get('/sources');
      setSources(response.data);
    } catch (error) {
      toast.error('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (providerKey) => {
    setConnecting(providerKey);

    try {
      const sessionResponse = await api.post('/integrations/nango/connect-session', null, {
        params: { provider: providerKey }
      });

      const responseData = sessionResponse.data;
      const provider_config_key = responseData.provider_config_key;

      if (!provider_config_key) {
        toast.error('Failed to get provider config key from backend');
        setConnecting(null);
        return;
      }

      const token = responseData.token || responseData.data?.token || responseData.connectSessionToken || responseData.sessionToken;

      if (!token) {
        toast.error('Failed to get session token from backend');
        setConnecting(null);
        return;
      }

      const { default: Nango } = await import('@nangohq/frontend');
      const nangoHost = process.env.REACT_APP_NANGO_HOST || 'https://api.nango.dev';

      const nango = new Nango({
        host: nangoHost,
        publicKey: 'empty'
      });

      const connectUI = nango.openConnectUI({
        apiURL: nangoHost,
        onEvent: (event) => {
          if (event.type === 'close') {
            setTimeout(() => { loadSources(); }, 1000);
            setConnecting(null);
          } else if (event.type === 'connect') {
            const connectionId = event.connectionId ||
              event.connection_id ||
              (event.payload && (
                event.payload.connectionId ||
                event.payload.connection_id ||
                event.payload.id
              ));

            if (!connectionId) {
              toast.error('Connected but no connection ID received.');
              setConnecting(null);
              loadSources();
              return;
            }

            const integrationName = integrations.find(i => i.providerKey === providerKey)?.name || providerKey;
            toast.success(`${integrationName} connected successfully!`);

            api.post('/integrations/nango/callback', null, {
              params: { connection_id: connectionId, provider: providerKey }
            }).then(() => {
              loadSources();
            }).catch(err => {
              toast.error(`Failed to save: ${err.response?.data?.detail || err.message}`);
            });

            setConnecting(null);
          } else if (event.type === 'error') {
            toast.error(`Connection failed: ${event.error || 'Unknown error'}`);
            setConnecting(null);
          }
        }
      });

      setTimeout(() => {
        connectUI.setSessionToken(token);
      }, 10);

    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || '';
      if (errorMessage.includes('not configured')) {
        toast.info('Nango not configured. Use mock data for testing.');
      } else {
        toast.error(`Connection failed: ${errorMessage}`);
      }
    } finally {
      setConnecting(null);
    }
  };

  const integrations = [
    { name: 'Slack', icon: Slack, color: 'bg-[#4A154B]', description: 'Send notifications to channels', providerKey: 'slack' },
    { name: 'Linear', icon: Workflow, color: 'bg-[#5E6AD2]', description: 'Sync issues and projects from Linear boards', providerKey: 'linear' },
    { name: 'Jira', icon: FileCode, color: 'bg-[#0052CC]', description: 'Sync issues and workflows', providerKey: 'jira' },
    { name: 'GitHub', icon: GitBranch, color: 'bg-[#181717]', description: 'Link pull requests and commits', providerKey: 'github' },
    { name: 'Google Drive', icon: FileCode, color: 'bg-[#1FA463]', description: 'Sync docs and folders', providerKey: 'google-drive' },
  ];

  // Group sources by type
  const sourcesByType = sources.reduce((acc, source) => {
    const type = source.source_type?.toLowerCase() || '';
    // Normalize type keys to match integration providerKeys
    const key = Object.keys(integrations.reduce((m, i) => { m[i.providerKey] = true; return m; }, {}))
      .find(k => type === k || type.includes(k)) || type;
    if (!acc[key]) acc[key] = [];
    acc[key].push(source);
    return acc;
  }, {});

  const getInstancesForType = (providerKey) => {
    return sourcesByType[providerKey] || [];
  };

  const handleDeleteClick = (instance) => {
    setInstanceToDelete(instance);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!instanceToDelete) return;
    setDeleting(true);
    try {
      const response = await api.delete(`/sources/${instanceToDelete.id}`);
      const affected = response.data.affected_projects || [];
      if (affected.length > 0) {
        toast.warning(
          `Deleted. ${affected.length} project(s) were using this source: ${affected.map(p => p.project_name).join(', ')}`,
          { duration: 6000 }
        );
      } else {
        toast.success('Integration deleted successfully');
      }
      setDeleteDialogOpen(false);
      setInstanceToDelete(null);
      loadSources();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete integration');
    } finally {
      setDeleting(false);
    }
  };

  const handleRenamed = (instanceId, newName) => {
    setSources(prev =>
      prev.map(s => s.id === instanceId ? { ...s, display_name: newName } : s)
    );
  };

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="integrations-page">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate('/settings')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground/90">Integrations</h1>
            <p className="text-muted-foreground">Manage your connected apps and services</p>
          </div>
        </div>

        {/* Info Card */}
        <Card className="p-4 glass-subtle">
          <div className="flex gap-3">
            <ExternalLink className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium mb-1">How to use integrations</h3>
              <div className="text-sm text-muted-foreground space-y-1">
                <p>• <strong>Mock Mode:</strong> Use "Generate Mock Events" in project sources to create test data instantly.</p>
                <p>• <strong>Real Mode:</strong> Click "Connect" to link your actual Slack, Jira, or GitHub via OAuth.</p>
                <p>• You can connect multiple accounts of the same type (e.g., two Slack workspaces).</p>
              </div>
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {integrations.map((integration) => {
            const Icon = integration.icon;
            const instances = getInstancesForType(integration.providerKey);
            const count = instances.length;
            const testIdBase = integration.name.toLowerCase().replace(/\s+/g, '-');

            return (
              <Card key={integration.name} className="p-6 hover:shadow-glass transition-shadow duration-200" data-testid={`integration-${testIdBase}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-lg ${integration.color} flex items-center justify-center`}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">{integration.name}</h3>
                      <p className="text-sm text-muted-foreground">{integration.description}</p>
                    </div>
                  </div>
                </div>

                {/* Status + Connect Button — always show Connect */}
                <div className="flex items-center gap-2 mb-3">
                  {count > 0 && (
                    <Badge variant="secondary">
                      {count} connected
                    </Badge>
                  )}
                  <Button
                    size="sm"
                    variant={count > 0 ? 'outline' : 'default'}
                    onClick={() => handleConnect(integration.providerKey)}
                    disabled={connecting === integration.providerKey}
                    data-testid={`connect-${testIdBase}-btn`}
                  >
                    {connecting === integration.providerKey ? (
                      'Connecting...'
                    ) : count > 0 ? (
                      <>
                        <Plus className="h-3.5 w-3.5 mr-1" />
                        Add Another
                      </>
                    ) : (
                      `Connect ${integration.name}`
                    )}
                  </Button>
                </div>

                {/* Connected instances list */}
                {count > 0 && (
                  <div className="space-y-2 border-t border-border pt-3">
                    {instances.map((instance) => (
                      <div
                        key={instance.id}
                        className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <InlineRenameInput
                          sourceId={instance.id}
                          value={instance.display_name || instance.source_type}
                          onRenamed={(newName) => handleRenamed(instance.id, newName)}
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => handleDeleteClick(instance)}
                          data-testid={`delete-instance-${instance.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
        </div>

        <div className="border-t border-border pt-6 mt-6">
          <h2 className="font-semibold mb-4 text-foreground/90">Go further</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="p-4 cursor-pointer hover:border-primary hover:shadow-glass transition-all duration-200">
              <h3 className="font-medium mb-1">Start guide</h3>
              <p className="text-sm text-muted-foreground">Quick tips for beginners</p>
            </Card>
            <Card className="p-4 cursor-pointer hover:border-primary hover:shadow-glass transition-all duration-200">
              <h3 className="font-medium mb-1">API documentation</h3>
              <p className="text-sm text-muted-foreground">Build custom integrations</p>
            </Card>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Integration?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{instanceToDelete?.display_name || instanceToDelete?.source_type}</strong>?
              <br /><br />
              <span className="text-destructive font-medium">
                This may affect projects that depend on this connection.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? 'Deleting...' : 'Delete Integration'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
